/**
 * UploadPreflightDialog — Feature A of
 * `ai-docs/lexide-promptbox-preflight-and-level-miniplan.md` (§2).
 *
 * The `[+]` on the composer opens THIS instead of the native picker. Unlike
 * ChatGPT/Claude, the intermediary step exists to PRESENT THE POSSIBILITIES:
 * what happens to an uploaded file, which types are accepted, what the
 * ceilings are. The lawyer then chooses files from here.
 *
 * Gate rulings this implements (§2.2):
 *   A-1 ★ ALWAYS shown. No "don't show again" — education-by-presentation is
 *         the purpose, and a dismiss-forever flag defeats it.
 *   A-2 ★ Only where upload is REAL (case scope). Describing the ingest
 *         pipeline where upload cannot happen would lie.
 *   A-4   NOT YET SATISFIED out of case — see `mode`. The historical `[+]`
 *         there still collects chips that go nowhere; correcting it needs the
 *         Global upload target, which is a runtime-side change.
 *
 * ⚠ DENSITY IS A REQUIREMENT, NOT A PREFERENCE (operator, 2026-08-02). The
 * first cut rendered numbered steps with icons and five bordered cards, and
 * ran past the fold on a laptop — a wall of instruction nobody reads is not education.
 * Everything here earns its line: ONE sentence for the pipeline, borderless
 * rows for the types, ONE line for the limits. If you add to this, take
 * something out.
 *
 * ⚠ COLOUR TOKENS RUN coffee→light / cream→dark, AND THE RAMPS DO NOT
 * OVERLAP. coffee is 500-950 only; cream is 50-500 only. There is no
 * `coffee-400` and no `cream-600`, and Tailwind emits NOTHING for an
 * undefined token — the text then inherits and paints PURE BLACK. That is
 * what `text-coffee-400 dark:text-cream-600` did to the extension lists here:
 * rgb(0,0,0) on rgb(26,23,20), a measured contrast of 1.18, invisible in dark
 * mode and never once erroring. Same family as tailwind-merge silently
 * dropping an unrecognised `text-*`. `color-tokens.test.ts` now fails the
 * build on any out-of-ramp shade.
 *
 * House pairs (measured): strong `text-coffee-900 dark:text-cream-100`, body
 * `text-coffee-700 dark:text-cream-300`, muted `text-coffee-500
 * dark:text-cream-500`. Small type needs MORE contrast, not less — the 11px
 * mono row uses `text-coffee-600 dark:text-cream-400`.
 *
 * ⚠ EVERY NUMBER AND TYPE COMES FROM THE SERVER (`useUploadLimits` →
 * `groupAcceptedExtensions` / `effectiveMaxBytes`). Nothing is hardcoded: the
 * UI has been the narrowest part of this pipe twice (a hardcoded
 * `application/pdf`, then a cap-table derivation that hid .txt/.md/.odt/.rtf)
 * and both times silently refused files the server would have accepted.
 *
 * ⚠ FAIL-SOFT. While the policy loads or is absent (older runtime) the
 * explainer still renders and the picker still opens; the server enforces
 * regardless.
 *
 * ⚠ WHAT THIS DOES NOT PROMISE. The pipeline line stops at "the agent can
 * cite and use the content". It deliberately does NOT say the chat confirms
 * receipt and reacts on completion: those are two halves on different
 * switches — the R1 receipt is ungated, the completion reaction env-gated OFF
 * — so claiming both would be false wherever the reaction is dark. Flagged by
 * `lexiun-agent-bus-plan` 2026-08-02; add it only when both ship together.
 */
import { useMemo } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useUploadLimits } from "@/hooks/useUploadLimits";
import { groupAcceptedExtensions } from "@/lib/upload-families";

const MB = 1024 * 1024;

/** "18,0 MB" — pt-BR decimal comma, matching `upload-safeguards`' phrasing. */
function mb(bytes: number): string {
  return `${(bytes / MB).toFixed(1).replace(".", ",")} MB`;
}

export interface UploadPreflightDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Only `case` today.
   *
   * ⚠ A `no-case` mode was built and REMOVED the same day (operator,
   * 2026-08-02). It told the lawyer to "open a case to send files", which is
   * FALSE: with no case selected, files upload to the Global ("Geral")
   * workspace by default. The copy was corrected rather than reworded away,
   * because when a claim is false the honest move is to delete it — and the
   * true replacement cannot be written yet: `ActiveSelection` carries no
   * global scope (on /chat it is `null` outright) and the runtime's
   * `document.upload` hard-requires clientId+matterId+caseId
   * (`gateway-methods/document.ts`, three requireParam early-returns). Wiring
   * the Global target is a two-layer change and the runtime half is not
   * mine. Until then the out-of-case `[+]` keeps its historical behaviour
   * rather than gaining a dialog that misinforms.
   */
  mode: "case";
  /** Fired by the primary CTA in `case` mode. */
  onChooseFiles: () => void;
}

export function UploadPreflightDialog({
  open,
  onOpenChange,
  mode,
  onChooseFiles,
}: UploadPreflightDialogProps) {
  const { t } = useTranslation();
  const { limits, isLoading } = useUploadLimits();

  const families = useMemo(
    () => (limits ? groupAcceptedExtensions(limits) : []),
    [limits],
  );

  const heading = t("uploadPreflight.title");

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-coffee-900/40 dark:bg-coffee-950/60" />
        <Dialog.Content
          data-testid="upload-preflight-dialog"
          data-mode={mode}
          aria-label={heading}
          className="fixed left-1/2 top-1/2 z-50 w-[26rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-cream-200/70 bg-white p-4 shadow-[0_8px_40px_rgba(28,24,20,0.18)] focus:outline-none dark:border-coffee-700/60 dark:bg-coffee-900"
        >
          <div className="mb-2.5 flex items-center justify-between gap-3">
            <Dialog.Title className="text-body-sm font-medium text-coffee-900 dark:text-cream-100">
              {heading}
            </Dialog.Title>
            <Dialog.Close
              data-testid="upload-preflight-close"
              aria-label={t("common.close")}
              className="shrink-0 rounded-md p-0.5 text-coffee-500 transition-colors hover:bg-cream-100 hover:text-coffee-800 dark:text-cream-500 dark:hover:bg-coffee-800 dark:hover:text-cream-200"
            >
              <X size={14} />
            </Dialog.Close>
          </div>

          {(
            <>
              {/* The pipeline in ONE sentence. The three numbered steps this
                  replaced said the same thing in 4× the height.

                  ⚠ P6 item 5 LOCKSTEP (agent-bus handoff §2): the summary
                  claims only the RECEIPT — the ungated [lexiun-ingest] line,
                  always true on the roll. The "assistant may comment on the
                  outcome" clause (the ⚖ D-8 reaction, gated on
                  BUS_CONCIERGE_POLL=1 AND BUS_MISSION_NOTIFY=1) was RETRACTED
                  2026-08-31: prod measured NOT YET ARMED — the DESIGNED
                  default-OFF posture (D-11/D-12 dampered launch; never armed,
                  nothing regressed) — so the possibility claim was not
                  realisable where lawyers would read it. REINSTATE ONLY on a
                  fresh ARMED prod measurement from `lexiun-agent-bus-plan`
                  (ask; they measure, never recall). Two traps for that
                  measurement: the switches are env (a fleet roll rebuilds the
                  env spec, so any reading expires on the next roll), and prod
                  Container Apps are named `lex-arc-adv-*` — a `lexruntime-*`
                  filter returns empty and reads as "no prod runtimes". */}
              <p
                data-testid="upload-preflight-pipeline"
                className="text-[13px] leading-snug text-coffee-700 dark:text-cream-300"
              >
                {t("uploadPreflight.pipeline.summary")}
              </p>

              <div className="mt-3">
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-coffee-500 dark:text-cream-500">
                    {t("uploadPreflight.types.heading")}
                  </span>
                  {limits && (
                    <span
                      data-testid="upload-preflight-limits"
                      className="shrink-0 text-[11px] tabular-nums text-coffee-500 dark:text-cream-500"
                    >
                      {t("uploadPreflight.limits.inline", {
                        count: limits.maxFilesPerBatch,
                        size: mb(limits.transportMaxBytes),
                      })}
                    </span>
                  )}
                </div>

                {families.length > 0 ? (
                  <ul
                    data-testid="upload-preflight-families"
                    className="divide-y divide-cream-200/70 dark:divide-coffee-700/50"
                  >
                    {families.map((f) => (
                      <li
                        key={f.key}
                        data-testid="upload-preflight-family"
                        data-family={f.key}
                        className="flex items-baseline gap-2 py-1"
                      >
                        <span className="w-[7.5rem] shrink-0 text-[12px] text-coffee-800 dark:text-cream-200">
                          {t(`uploadPreflight.family.${f.key}`)}
                        </span>
                        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-coffee-600 dark:text-cream-400">
                          {f.extensions.join(" ")}
                        </span>
                        <span
                          data-testid="upload-preflight-family-cap"
                          className="shrink-0 text-[11px] tabular-nums text-coffee-500 dark:text-cream-500"
                        >
                          {/* The family's SMALLEST effective cap — quoting the
                              larger one would hold for only some members. */}
                          {f.capVaries
                            ? t("uploadPreflight.capUpTo", { size: mb(f.capBytes) })
                            : mb(f.capBytes)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  // Fail-soft: policy loading, or an older runtime that does
                  // not publish it. The server enforces either way.
                  <p
                    data-testid="upload-preflight-types-generic"
                    className="text-[12px] text-coffee-500 dark:text-cream-500"
                  >
                    {isLoading
                      ? t("uploadPreflight.types.loading")
                      : t("uploadPreflight.types.generic")}
                  </p>
                )}
              </div>

              <p className="mt-2 text-[11px] leading-snug text-coffee-500 dark:text-cream-500">
                {t("uploadPreflight.pipeline.archiveNote")}
              </p>

              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  data-testid="upload-preflight-choose"
                  onClick={onChooseFiles}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-coffee-800 px-3 py-1.5 text-[13px] font-medium text-cream-50 transition-colors hover:bg-coffee-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:bg-cream-100 dark:text-coffee-900 dark:hover:bg-white"
                >
                  <Upload size={13} />
                  {t("uploadPreflight.choose")}
                </button>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default UploadPreflightDialog;
